/**
 * @author Hiroki Moto
 * @description API = {TYPE} + Name
 * @example postCareerProgram
 */

import apisauce from 'apisauce';
import includes from 'ramda/src/includes.js';
import isNil from 'ramda/src/isNil';
import isEmpty from 'ramda/src/isEmpty';
import { store } from '~/reducers';
import AppActions from '~/actions/app';
import { API_BASE_URL, S3_DEV_API, S3_PROD_API, S3_URL } from './constants';
import decamelize from '~/services/decamelize';

const TIMEOUT = 100000;
const isProd = import.meta.env.VITE_PROD === 'true';

const Config = {
    API_URL: import.meta.env.VITE_API_BASE_URL || API_BASE_URL,
    S3_URL: import.meta.env.VITE_S3_URL || S3_URL,
    S3_API: isProd ? import.meta.env.VITE_S3_PROD_BUCKET || S3_PROD_API : import.meta.env.VITE_S3_DEV_BUCKET || S3_DEV_API,
};

const authenticated = (api) => {
    const token = localStorage.getItem('token');
    api.setHeader('Authorization', `Bearer ${token}`);
    return api;
};

const naviMonitor = (response) => {
    // Handle new axios 1.x error structure where errors are wrapped in response property
    const actualResponse = response.response || response;
    const status = actualResponse?.status;
    const data = actualResponse?.data;
    const config = actualResponse?.config || response.config;

    if (status === 401 && !(data?.message && includes('Unauthorized', data.message))) {
        store.dispatch(AppActions.logoutRequest());
        window.location.href = '/login';
    }
};

const create = (baseURL = Config.API_URL) => {
    const api = apisauce.create({
        baseURL,
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        timeout: TIMEOUT,
    });

    const s3API = apisauce.create({
        baseURL: Config.S3_URL,
        headers: {
            Accept: 'application/xml',
            'Content-Type': 'multipart/form-data'
        },
        timeout: TIMEOUT,
    });

    const vendorAPI = apisauce.create({
        baseURL: 'https://cors-anywhere.herokuapp.com/https://www.drivingsales.com/',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        timeout: TIMEOUT,
    });

    api.addMonitor(naviMonitor);
    api.addRequestTransform((request) => {
        if (request.data) request.data = decamelize(request.data);
    });

    /**
     * POST Quota Actuals Save
     * @param {*} payload
     */
    const postQuotaActualsSave = (payload) => authenticated(api).post('quota/actuals', payload);
    /**
     * GET Career Program
     * @param {number} programId
     */
    const getCareerProgram = (programId) => authenticated(api).get(`program/${programId}`);
    /**
     * POST Program Save
     * @param {any} payload
     */
    const postCareerProgram = (payload) => authenticated(api).post('develop/library/program/save', payload);
    /**
     * POST Check Password
     * @param {number} user_id
     * @param {string} password
     */
    const postCheckPassword = (payload) => authenticated(api).post('auth/checkpassword', payload);

    /**
     * POST Training Report Companies/Individuals
     * @param {*} payload
     * @param {string} type /* company | individual
     */
    const postTrainingReport = (payload, type) => authenticated(api).post(`reports/${type}/training`, payload);
    /**
     * GET Generate Hash
     * @param {*} payload
     */
    const getGenerateHash = () => authenticated(api).get('media/hash');
    /**
     * POST Presentation Save
     * @param {*} payload
     */
    const postPresentationSave = (payload) => authenticated(api).post('develop/library/presentation/save', payload);
    /**
     * GET Quota Actuals Count
     * @param {number} userId
     * @param {number} year
     * @param {number} month
     */
    const getActualsCount = (userId, month, year) => authenticated(api).get(`develop/quotastats/${userId}?month=${month}&year=${year}`);
    /**
     * Create a Quota Actual Comment
     * @param {*} payload
     */
    const postQuotaActualComment = (payload) => authenticated(api).post('quota/actuals/comment', payload);
    /**
     * Edit all comments on a Quota Actual.
     * @param {*} payload
     */
    const postQuotaActualComments = (payload) => authenticated(api).post('quota/actuals/comments', payload);
    /**
     * POST Sign Off
     * @param {number} performance_id
     * @param {number} user_id
     * @param {string} email
     * @param {string} password
     */
    const postSignOff = (payload) => authenticated(api).post('performance/signoff', payload);
    /**
     * POST Terminate Employee
     * @param {number} company_id
     * @param {any} payload
     */
    const postTerminateEmployee = (companyId, payload) => authenticated(api).post(`company/${companyId}/users/terminate`, payload);
    /**
     * GET Get Company
     * @param {number} companyId
     */
    const getGetCompany = (companyId) => authenticated(api).get(`vr/companies/${companyId}`);
    /**
     * GET Get VR Company
     * @param {number} companyId
     */
    const getVRCompany = (companyId) => authenticated(api).get(`vr/company/${companyId}`);
    /**
     * Get Get Company based on Date Range
     * @param {string} options 'date_start=2020-01-01&date_end=2020-12-3'
     */
    const getGetCompanyWithDate = (companyId, options) => authenticated(api).get(`vr/companies/${companyId}?${options}`);
    /**
     * GET Recently Updated Products
     * @param {*} payload
     */
    const getRecentlyUpdatedProducts = (payload) => authenticated(api).get(`vr/products/recent?${payload}`);
    /**
     * GET Popular Products
     * @param {*} payload
     */
    const getPopularProducts = () => authenticated(api).get('vr/products/popular');
    /**
     *
     * @param {*} options filter options like search=dealer
     * @returns vendor products
     */
    const getVRProducts = (options) => authenticated(api).get(`vr/products?${options}`);
    /**
     * POST Multi-Company Employees List
     * @param {*} payload
     */
    const postMultiCompanyEmployees = (payload) => authenticated(api).post('employees', payload);
    /**
     *
     * @param {*} mode one of company and user
     * @param {*} id company id or user id
     */
    const getGetBlog = (mode, id) => {
        if (mode === 'company') {
            return authenticated(api).get(`com/blog?company_id=${id}`);
        }
        return authenticated(api).get(`com/blog?user_id=${id}`);
    };
    /**
     * POST Create Blog
     * @param {*} payload
     */
    const postCreateBlog = (payload) => authenticated(api).post('com/blog/create', payload);
    /**
     * POST Add User
     * @param {*} payload
     */
    const postAddUser = (payload) => authenticated(api).post('business/user/add', payload);
    /**
     * POST Remove User
     * @param {*} payload
     */
    const postRemoveUser = (payload) => authenticated(api).post('business/user/remove', payload);
    /**
     * POST Save Product
     * @param {*} payload
     */
    const postSaveProduct = (payload) => authenticated(api).post('vr/product/save', payload);
    /**
     * POST Save Content
     * @param {*} payload
     */
    const postSaveContent = (payload) => authenticated(api).post('content/save', payload);
    /**
     * POST Save Product Media
     * @param {*} payload
     */
    const postSaveProductMedia = (payload) => authenticated(api).post('vr/product/media/save', payload);
    /**
     * Delete Product Media
     * @param {*} payload
     */
    const deleteProductMedia = (payload) => authenticated(api).post('vr/product/media/delete', payload);
    /**
     * POST Multi-Company Data
     * @param {*} payload
     */
    const postMultiCompanyData = (payload) => authenticated(api).post('company/data', payload);
    /**
     * GET All Authors
     * @param {*} payload
     */
    const getAllAuthors = () => authenticated(api).get('content/authors');
    /**
     * POST Add/Save Author
     * @param {*} payload
     */
    const postSaveAuthor = (payload) => authenticated(api).post('content/authors/save', payload);
    const postSaveBusinessAuthor = (payload) => authenticated(api).post('business/authors/save', payload);
    /**
     * POST Delete Author
     * @param {*} options
     */
    const postDeleteAuthor = (payload) => authenticated(api).post('content/authors/delete', payload);

    /**
     * POST Category Save
     * @param {*} payload
     */
    const postCategorySave = (payload) => authenticated(api).post('content/community/categories/save', payload);

    /**
     * POST Category Delete
     * @param {*} payload
     */
    const postCategoryDelete = (payload) => authenticated(api).post('content/community/categories/delete', payload);

    /**
     * POST Category Restore
     * @param {*} payload
     */
    const postCategoryRestore = (payload) => authenticated(api).post('content/community/categories/restore', payload);

    /**
     * POST Vendor Rating Save
     * @param {*} payload
     */
    const postVendorRating = (payload) => authenticated(api).post('content/ratings/save', payload);

    /**
     * POST Add/Edit Departments
     * @param {*} company_id
     * @param {*} payload
     * @returns
     */
    const postAddEditDepartments = (companyId, payload) => authenticated(api).post(`company/${companyId}/departments/save`, payload);
    /**
     * POST Delete Department
     * @param {*} options
     * @returns
     */
    const postDeleteDepartment = (companyId, departmentId) => authenticated(api).post(`company/${companyId}/departments/${departmentId}/delete`);
    /**
     * GET Get Business
     * @param {*} payload
     */
    const getGetBusiness = (options) => authenticated(api).get(`business?${options}`);
    /**
     * POST Add/Edit Teams
     * @param {*} payload
     */
    const postAddEditTeams = (companyId, payload) => authenticated(api).post(`company/${companyId}/teams/save`, payload);
    // POST DELETE Team
    const postDeleteTeam = (companyId, teamId) => authenticated(api).post(`company/${companyId}/teams/${teamId}/delete`);
    /**
     * POST Save Job Roles & Titles
     * @param {*} payload
     */
    const postSaveJobRoles = (companyId, payload) => authenticated(api).post(`company/${companyId}/jobroles/save`, payload);
    /**
     * POST Save Company
     * @param {*} payload
     */
    const postSaveHCMCompany = (payload) => authenticated(api).post('company/save', payload);
    /**
     * POST Save Company
     * @param {*} payload
     */
    const postSaveVRCompany = (payload) => authenticated(api).post('vr/company/save', payload);
    /**
     * GET Employees List (optimized)
     * @param {*} payload
     */
    const getMinimalCompanyEmployees = (companyId, cacheBust) => authenticated(api).post(`employees`, { companyId, cacheBust });
    /**
     * GET Get Topics
     * @param {*} payload
     * @param {string} order // "new"(default), "activity", "popular", or "recent"
     */
    const getGetTopics = (options) => authenticated(api).get(`com/topic?${options}`);
    const getAllDepartmentTopics = () => authenticated(api).get('com/topics');
    /**
     * GET Edit Featured Items
     * @param {string} type / post, topic, user, company, product
     */
    const getEditFeaturedItems = (type, options) => authenticated(api).get(`com/featured/${type}/edit?${options}`);
    /**
     * GET Edit Featured Items without authentication
     * @param {string} type / post, topic, user, company, product
     */
    const getFeaturedItems = (type = 'post', options) => api.get(`com/featured/${type}?${options}`);
    /**
     * @description save featured item API
     * @param {string} type one of 'post', 'topic', 'user', 'company', 'product'
     * @param {*} payload featured item object
     */
    const postSaveFeaturedItem = (type = 'post', payload) => api.post(`com/featured/${type}/save`, payload);
    /**
     * @description archieve featued item API
     * @param {*} featured_id
     */
    const postArchiveFeaturedItem = (featured_id) => api.post(`com/featured/${featured_id}/archive`);
    /**
     * GET Get Blog Posts
     * @param {*} options filter options based on blog id, category id, company id, user id, blog categories, and search
     * @description Get blog posts filtered by blog_id, category_id, company_id, user_id, blog categories, and search
     * @example /api/com/blogs?company_id=274373
     */
    const getBlogPosts = (options) => api.get(`com/blogs?${options}`);
    /**
     * GET Get Blog Categories
     */
    const getComBlogCategories = () => authenticated(api).get('com/blog/categories');
    /**
     * Get Learn Feeds
     * @param {string} type feed type (one of training, habits, quotas, scorecards, programs, tracks, performances, habitschedules, tasks, assigned)
     * @param {number} userId user ID
     * @param {string} options query string such as per_page, page, assigned, order, sort, date_start, date_end, card_type_id, archived and etc
     */
    const getLearnFeeds = (type, userId, options) => authenticated(api).get(`/develop/${type}/${userId}?${options}`);
    /**
     * GET Category Sponsored Products
     * @param {*} payload
     */
    const getCategorySponsoredProducts = (categoryId) => authenticated(api).get(`vr/categories/${categoryId}/products/sponsored`);
    /**
     * GET Quotas Actuals Feed
     * @param {number} userId
     * @param {string} options
     */
    const getQuotasActuals = (userId, options) => authenticated(api).get(`develop/actuals/${userId}?${options}`);
    /**
     * GET Company Performance Reviews
     * @param {*} payload
     */
    const getCompanyPerformanceReviews = (options) => authenticated(api).get(`/performance/users?${options}`);
    /**
     * GET Company Checkouts
     * @param {*} payload
     */
    const getReportCheckouts = (options) => authenticated(api).get(`reports/checkouts?${options}`);
    /**
     * GET Company Checkouts
     * @param {*} payload
     */
    const getIndividualCheckout = (options) => authenticated(api).get(`/checkout?${options}`);
    /**
     * GET Company Performance Reviews Stats
     * @param {*} payload
     */
    const getCompanyPerformanceReviewStats = (options) => authenticated(api).get(`/performance/report?${options}`);
    /**
     * GET Company Daily Checkouts
     * @param {*} payload
     */
    const getCompanyDailyCheckouts = (options) => authenticated(api).get(`checkout/users?${options}`);
    /**
     * GET Individual Performance Review
     * @param {*} payload
     */
    const getIndividualPerformanceReview = (options) => authenticated(api).get(`performance?${options}`);
    /**
     * GET Performance Reviews Feed
     * @param {number} id
     * @param {string} options
     */
    const getPerformanceReviews = (id, options) => authenticated(api).get(`/develop/performances/${id}?${options}`);
    /**
     * GET Tasks Feed
     * @param {*} payload
     */
    const getTasksFeed = (userId, options) => authenticated(api).get(`develop/tasks/${userId}?${options}`);
    /**
     * GET User Habits
     * @param {number} userId
     * @param {string} options
     */
    const getUserHabits = (userId, options) => authenticated(api).get(`develop/habits/${userId}?${options}`);
    /**
     * GET User Tasks Counts
     * @param {*} payload
     * @param {string} options
     */
    const getUserTasksCounts = (userId, options) => authenticated(api).get(`users/${userId}/taskcounts?${options}`);
    /**
     * POST Library Card Templates
     * @param {*} templates
     * @param {*} options
     */
    const postLibraryCardTemplates = (templates, options = '') => authenticated(api).post(`develop/library?${options}`, templates);
    /**
     * GET Library Courses/Modules/Programs/Quotas/Scorecards/Tracks Search
     * @param {string} type // courses, modules, programs, quota_templates, tracks, scorecards
     * @param {string} options // author_id[]=1&author_id[]=2
     */
    const getLibraryTemplatesSearch = (type = 'courses', options) => authenticated(api).get(`develop/library/${type}/search?${options}`);
    /**
     * POST Scorecard Save
     */
    const postScorecardSave = (payload) => authenticated(api).post('develop/library/scorecard/save', payload);
    /**
     * POST Delete Scorecard
     * @param {*} payload
     * Example: {"scorecard": {"id":5} }
     */
    const postDeleteScorecard = (payload) => authenticated(api).post('scorecard/delete', payload);
    /**
     * GET Users Employee Search
     * @param {*} options
     * Example: search=jared&department_id=1&team_id=1&job_role_id=1
     */
    const getEmployeeSearch = (options) => authenticated(api).get(`users/search?${options}`);

    /**
     * GET Company Org Chart
     * @param {*} companyId
     * Example: companyId=1
     */
    const getCompanyOrgChart = (companyId) => authenticated(api).get(`company/${companyId}/orgchart`);
    /**
     * GET Save Actuals Report
     * @param {*} options
     * Example: company_id[]=5&date_start=2021-02-01
     */
    const getSaveActualsReport = (options) => authenticated(api).get(`reports/company/actuals?${options}`);
    /**
     * GET Checkout Actuals
     * @param {*} options
     * Example: company_id[]=5&date_start=2021-02-01
     */
    const getCheckoutActuals = (options) => authenticated(api).get(`checkout/orgchart?${options}`);
    /**
     * GET Checkout OrgChart
     * @param {*} options
     * Example: company_id[]=5&date_start=2021-02-01
     */
    const getCheckoutOrgChart = (options) => authenticated(api).get(`checkout/orgchart?${options}`);
    /**
     * GET User Job Description
     * @returns
     */
    const getUserJobDescription = () => api.get('com/user/jobdescriptions');
    /**
     * @param {number} companyId
     * @param {string} base
     * @param {string} options
     * { order_by: 'name'|'company'|'departments'|'anniversary_date', order: 'ASC'|'DESC', month_start, month_end }
     * @returns
     */
    const getAnniversaryReports = (companyId, base, options) => authenticated(api).get(`reports/company/${companyId}/employees/${base}?${options}`);
    /**
     *
     * @param {string} options
     * @returns
     */
    const getProjectStats = (options) => authenticated(api).get(`projects?${options}&stats=1`);
    /**
     * GET Authoring
     * @returns
     */
    const getAuthoring = () => authenticated(api).get('authoring');
    /**
     * GET Library Filters
     * @returns
     */
    const getLibraryFilters = () => authenticated(api).get('/develop/library/filters');
    /**
     * GET GET Entity Connections
     * @param {*} options
     * @returns
     */
    const getEntityConnections = (options) => authenticated(api).get(`com/entity/connections?${options}`);
    /**
     * POST Entity Connection Request
     * @param {*} payload
     * @returns
     */
    const postEntityConnection = (payload) => authenticated(api).post('com/entity/connect', payload);
    /**
     * POST Connection Request
     * @param {*} payload
     * @returns
     */
    const postConnection = (payload) => authenticated(api).post('com/connect', payload);
    /**
     *
     * @returns
     */
    const getCompanies = () => authenticated(api).get('company');
    /**
     * GET VR Company/User Slug
     * @param {string} slug
     * @returns
     */
    const getCompanySlug = (slug) => authenticated(api).get(`slug/${slug}`);
    /**
     * GET Blog Slug
     * @param {string} entity
     * @param {string} post
     * @returns
     */
    const getBlogSlug = (entity, post) => authenticated(api).get(`slug/${entity}/blog/${post}`);

    // Deprecated API Definitions

    //////////////////////////////////////////// App
    // Login API
    const loginByEmail = (payload) => api.post('auth/login', payload);
    const fileUpload = (payload) => s3API.post(Config.S3_API, payload);
    const getCompanyVendor = (vendorId) => vendorAPI.get(`/productsByCompany?id=${vendorId}`);

    // Recover Password
    const postRecoveryPassword = (payload) => api.post('auth/recovery', payload);

    //////////////////////////////////////////// Develop

    // Learn
    const getLearnFeed = (userId, status, perPage = 10, page = 1, dateStart, dateEnd, assignType, order = 'due_at', sort = 'ASC') =>
        authenticated(api).get(`develop/training/${userId}?${status}&per_page=${perPage}&page=${page}&assigned=${assignType}&order=${order}&sort=${sort}&date_start=${dateStart}&date_end=${dateEnd}`);
    const getAssignedCards = (option) => authenticated(api).get(`develop/assigned?${option}`);
    const getQuizQuestion = (quizId) => authenticated(api).get(`assessments/question/${quizId}`); // get question by quiz(assessment) id
    const submitAnswer = (quizId, payload) => authenticated(api).post(`assessments/answer/${quizId}`, payload);
    const getS3Info = () => authenticated(api).get('media/s3info');
    const resetQuiz = (quizId) => authenticated(api).post(`assessments/reset/${quizId}`);

    // user avatar upload
    const uploadAvatar = (payload) => authenticated(api).post('cropper/crop', payload);

    // APIs related to cards, need to refactor these APIs
    const updateCard = (event, cardId) => authenticated(api).post(`cards/${cardId}/event/${event}`);
    const getScormLink = (cardId) => authenticated(api).get(`cards/${cardId}/scormlink`);
    const getCardTemplate = (templateId) => authenticated(api).get(`cards/template/${templateId}`);
    const updateModule = (payload) => authenticated(api).post(`cards/${payload.id}/update`, payload.data);
    const createCard = (payload) => authenticated(api).post('cards/create', payload);
    const assignTraining = (payload) => authenticated(api).post(`/cards/assign`, payload);
    const assignScorecard = (payload) => authenticated(api).post('scorecard/assign', payload);
    /**
     * update card instance
     * @param {*} cardInstanceId card instance id
     * @param {*} payload updated card data
     * @param {*} event one of 'delete' and 'update'
     */
    const updateCardInstance = (cardInstanceId, event = '', payload = null) => {
        if (isEmpty(event)) {
            return authenticated(api).get(`cards/${cardInstanceId}`);
        }
        return authenticated(api).post(`cards/${cardInstanceId}/${event}`, payload);
    };

    const postUnassignContent = (payload) => authenticated(api).post('/unassign', payload);

    // Career
    const getCareerMap = (companyId) => authenticated(api).get(`standards/${companyId}`);
    const getCareerGoals = (userId) => authenticated(api).get(`users/${userId}/goals`);
    const postCareerGoals = (userId, data) => authenticated(api).post(`users/${userId}/goals/save`, data);
    const getCareerDevReports = (companyId, userId) => authenticated(api).get(`develop/career/${companyId}/${userId}`);
    const requestPromote = (companyId, userId) => authenticated(api).post(`company/${companyId}/user/${userId}/requestpromotion`);

    // Reports
    const getUserReports = (userId, companyId, dateStart, dateEnd) => authenticated(api).get(`users/${userId}/user_development_report/${companyId}?date_start=${dateStart}&date_end=${dateEnd}`);
    const getUsersCourseRequest = (userId, dateStart, dateEnd) => authenticated(api).get(`users/${userId}/course_list?per_page=5000&page=1&date_start=${dateStart}&date_end=${dateEnd}`);
    const getTrainingReports = (payload) => authenticated(api).post('reports/training', payload);
    const getCompanyCareers = (companyId) => authenticated(api).get(`users/career_report/${companyId}`);
    const getFeedsReports = (userId, sort) => authenticated(api).get(`develop/learn/${userId}?per_page=5000&page=1&assigned=${sort}`);
    // GET Certification Reports
    const getCertificationReport = (type, options) => authenticated(api).get(`reports/certification/${type}?${options}`);
    // get career reports
    const getCareerReports = (type, options) => authenticated(api).get(`reports/career/${type}?${options}`);
    // get company new hire orientation report
    const postHireOrientationReport = (payload) => authenticated(api).post('reports/individual/newhires', payload);
    // get quota report
    const postQuotaReports = (payload) => authenticated(api).post('reports/company/quotas', payload);
    // get Competency report
    const postTrainingCompetencyReports = (payload) => authenticated(api).post('reports/competency', payload);
    // get Individual Quota report
    const postIndividualQuotaReport = (userId, payload) => authenticated(api).post(`develop/quotas/${userId}`, payload);
    /**
     * Get Tracks Reports
     * @param {*} payload {"company_id":[5],"date_start":"2021-03-01","date_end":"2021-03-30"}
     */
    const postTrackReports = (payload) => authenticated(api).post('reports/tracks', payload);
    const postTeamActivityReport = (payload) => authenticated(api).post('reports/team/activity', payload);

    // Archive
    const getArchivedFeeds = (userId, perPage = 10, page = 1) => authenticated(api).get(`develop/learn/${userId}?card_type_id=1&archived=1&per_page=${perPage}&page=${page}`);

    const getDevLibrarySearch = (type, url, perPage, page) => authenticated(api).get(`develop/library/${type}/search?per_page=${perPage}&page=${page}${url}`);
    const getDevLibraryCourses = () => authenticated(api).get('develop/library/courses?author_id[]=1');
    const getDevLibraryModules = () => authenticated(api).get('develop/library/modules?author_id[]=1');
    const getDevLibraryTracks = () => authenticated(api).get('develop/library/tracks?author_id[]=1');
    // postDevLibraryTemplateSave, postDevLibraryTrackSave
    const postDevLibraryCardSave = (payload, type) => authenticated(api).post(`develop/library/${type}/save`, payload);
    const getDevLibrarySearchModules = (options, type) => authenticated(api).get(`develop/library/${type}/search?${options}`);
    const postLibraryTrackSave = (payload) => authenticated(api).post('develop/library/track/save', payload);
    const getDevReportTrainingList = ({ options, user_id }) => authenticated(api).post(`develop/library/tracks?type=3&${options}`, { user_id });
    const getDevReportAuthors = () => authenticated(api).get('develop/library/counts');
    const postDevLibraryAddDocument = (payload) => authenticated(api).post('develop/library/document/save', payload);
    const postDevLibraryDeleteDocument = (documentId) => authenticated(api).post(`develop/library/documents/delete/${documentId}`);
    const postDevLibraryCreateQuota = (payload) => authenticated(api).post('develop/library/quota_template/save', payload);
    const getLibrarySearch = (mode, options) => authenticated(api).get(`develop/library/${mode}?${options}`);
    const getQuotaOptions = () => authenticated(api).get(`quota/filters`);
    const getLibraryList = (companyId, type, options) => authenticated(api).get(`develop/library/${type}/list/${companyId}?${options}`);

    const getTemplateCheck = (templateId) => authenticated(api).get(`develop/library/template/check/${templateId}`);

    /**
     * @param {string} type library type (required) one of 'track', 'template', 'program', 'quota_template', 'scorecard', 'assessment'
     * @param {string} mode event mode (required) - one of 'delete', 'save'
     * @param {number} cardId template id (optional)
     * @param {object} payload post data (optional)
     */
    const postDevLibraryEvent = (type, mode, cardId = null, payload = null) => {
        if (isNil(cardId)) {
            return authenticated(api).post(`develop/library/${type}/${mode}`, payload);
        }
        return authenticated(api).post(`develop/library/${type}/${mode}/${cardId}`, payload);
    };

    /**
     * Delete program Careers and Certifications
     * @param {number} program_id program_id of program to be deleted
     */
    const postDeleteLibraryEvent = (program_id) => authenticated(api).post(`develop/library/program/delete/${program_id}`);

    /**
     *  Track Check Delete API
     * @param {number} track_id  track_id of track to be deleated
     */
    const getTrackCheck = (track_id) => authenticated(api).post(`develop/library/track/check/${track_id}`);

    /**
     *  Quota Template Delete Check API
     * @param {number} quota_template_id quota_template_id of quota to be deleted
     */
    const getQuotaCheck = (quota_template_id) => authenticated(api).post(`develop/library/quota_template/check/${quota_template_id}`);

    /** Assessment View API
     *@param {number} assessmant_id
     */
    const getAssessments = (assessment_id) => authenticated(api).post(`develop/library/assessments/view/${assessment_id}`);

    const getDailyFeed = (userId) => authenticated(api).post(`develop/daily/${userId}`);

    const getCheckout = (options) => authenticated(api).get(`checkout?${options}`);

    const postAddCheckoutComment = (payload) => authenticated(api).post('checkout/comment', payload);

    const postCheckoutEdit = (payload) => authenticated(api).post('checkout/edit', payload);

    const postCheckoutSignOff = (payload) => authenticated(api).post('checkout/signoff', payload);

    const postCheckoutSkipDay = (payload) => authenticated(api).post('checkout/skip', payload);

    const postCheckoutReset = (payload) => authenticated(api).post('checkout/reset', payload);

    const postCheckoutApprove = (payload) => authenticated(api).post('checkout/approve', payload);

    /**
     * program event API
     * @param {*} event one of 'start', 'stop', 'nextlevel', 'delete'
     * @param {*} payload user_id and program_id
     */
    const postProgramEvent = (event, payload) => authenticated(api).post(`program/${event}`, payload);
    const getProgramRequestPromotion = (programId) => authenticated(api).get(`program/${programId}/requestpromotion`);
    const getProgramStats = (programId) => authenticated(api).get(`program/${programId}/stats`);
    const getUserPrograms = (userId, options) => authenticated(api).get(`develop/programs/${userId}?${options}`);
    const getUserScorecards = (userId) => authenticated(api).get(`develop/scorecards/${userId}`);

    // need to refactor getting develop library list apis below this comment with 'getLibraryList'.
    const getDevLibraryCourseList = (companyId) =>
        authenticated(api).get(
            `develop/library/courses/list/${companyId}?card_type_id[]=2&card_type_id[]=3&card_type_id[]=4&card_type_id[]=5&card_type_id[]=6&card_type_id[]=7&card_type_id[]=8&card_type_id[]=9&card_type_id[]=12&card_type_id[]=13&card_type_id[]=14&card_type_id[]=15`
        );
    const getDevLibraryModuleList = (options) => authenticated(api).get(`develop/library/modules/list/${options}`);
    const getDevLibraryTrackList = (companyId) => authenticated(api).get(`develop/library/tracks/list/${companyId}`);
    const getDevLibraryProgramsList = (companyId) => authenticated(api).get(`develop/library/programs/list/${companyId}`);
    // GET Manager Dashboard
    const getManagerDashboard = (companyId, dateStart, dateEnd) => authenticated(api).get(`reports/manager/dashboard?company_id=${companyId}&date_start=${dateStart}&date_end=${dateEnd}`);
    ////////////////////////////////////////////////////

    // Records
    const postDevRecordsCompany = (payload) => authenticated(api).post('company/save', payload);

    //////////////////////////////////////////// Manage

    const getUsers = (companyId) => authenticated(api).get(`company/${companyId}/employees`);
    const addComment = (payload) => authenticated(api).post(`/comments/create`, payload);
    const getEmployees = (companyId) => authenticated(api).get(`users/manages/list/${companyId}`);

    // Add Project
    const postMngProjectsAddNew = (payload, companyId) => authenticated(api).post(`company/${companyId}/projects/add`, payload);

    // Update Project
    const postMngProjectsUpdate = (payload, companyId, projectId) => authenticated(api).post(`company/${companyId}/projects/${projectId}/update`, payload);

    // Delete Project
    const postMngProjectsDelete = (projectId, companyId) => authenticated(api).post(`company/${companyId}/projects/${projectId}/delete`);

    // get Authors
    const getAuthors = (companyId) => authenticated(api).get(`develop/library/list/${companyId}`);
    //get Courses
    const getCourses = (payload, perPage) => authenticated(api).post(`develop/library/search?per_page=${perPage}`, payload);

    const searchCourses = (payload) => authenticated(api).post(`develop/library/search?`, payload);

    const getProjectTasks = (companyId, projectId) => authenticated(api).get(`company/${companyId}/projects/${projectId}/tasks`);

    const getUserProjectTasks = (userId, options) => authenticated(api).get(`develop/tasks/${userId}?${options}`);
    const getUserProjecthabits = (userId, options) => authenticated(api).get(`develop/habits/${userId}?per_page=5000&${options}`);

    // get individual tasks
    const getUsertasks = (userId, dateStart, dateEnd) => authenticated(api).get(`/users/${userId}/taskcounts?date_start=${dateStart}&date_end=${dateEnd}`);
    // get company tasks
    const getCompanytasks = (companyId, dateStart, dateEnd) => authenticated(api).get(`/users/manages/${companyId}/taskcounts?date_start=${dateStart}&date_end=${dateEnd}`);

    // POST Engagement: Tasks Report Individuals
    const getIndividualTasks = (payload) => authenticated(api).post('reports/individual/tasks', payload);

    // POST Performance Review Save
    const postPerformanceReview = (payload) => authenticated(api).post('performance/save', payload);

    // POST Performance Review Reset
    const postPerformanceReviewReset = (payload) => authenticated(api).post('performance/reset', payload);

    /**
     * POST Engagement: <...> Reports
     * @param {*} payload
     * @param {*} by company | individual
     * @param {*} type trainings | newhires | performances | tasks | careers | records | users
     */
    const postMngReportsEngagementReport = (payload, by, type) => authenticated(api).post(`/reports/${by}/${type}`, payload);

    // POST Edit Profile
    const postEditProfile = (userId, payload) => authenticated(api).post(`/users/${userId}/profile/save`, payload);

    // GET user companies
    const getUserCompanies = (userId) => authenticated(api).get(`/users/${userId}/companies`);

    /**
     * GET Scorecard instance
     * @param {number} scorecardId
     */
    const getScorecard = (scorecardId) => authenticated(api).get(`scorecard/${scorecardId}`);

    /**
     * Endpoints to get company info such as users, departments, job roles, etc.
     */
    const getCompany = (companyId) => authenticated(api).get(`company/${companyId}`);
    const getCompanyUsers = (companyId, perPage = 50, currentPage = 1) => authenticated(api).get(`company/${companyId}/users?per_page=${perPage}&page=${currentPage}`);
    const postCompanyUsers = (payload, companyId) => authenticated(api).post(`company/${companyId}/users/save`, payload);

    // Notifications
    const getNotifications = (options) => authenticated(api).get(`notifications${options}`);
    const updateNotifications = (id, type) => authenticated(api).post(`notifications/${id}/${type}`);

    // Notifications Settings
    const getDevRecordsNotificationsSettings = () => authenticated(api).get('notifications/settings');
    const postDevRecordsNotificationsSettings = (payload) => authenticated(api).post('notifications/settings/save', payload);

    // Terminate, Unterminate and Delete user
    const postTerminateUser = (payload, companyId, event) => authenticated(api).post(`company/${companyId}/users/${event}`, payload);
    const postDeleteUser = (userId) => authenticated(api).post(`users/${userId}/delete`);

    // Vendor
    const getVendorCategories = () => authenticated(api).get('vr/categories');
    const getCategoryPopularProducts = (categoryId) => authenticated(api).get(`vr/categories/${categoryId}/products/popular`);
    const getAllCategoryPopularProducts = () => authenticated(api).get('vr/categories/products');
    const getCategoryProducts = (category_id, options = '') => authenticated(api).get(`vr/categories/${category_id}/products?${options}`);

    // Get vendor company detail
    const postBusinessCompany = (payload) => authenticated(api).post('business/save', payload);
    const getVendorCompaniesAlphabet = (options = 'alphabet') => authenticated(api).get(`vr/company/${options}`);
    const getCompanyBusiness = (options) => authenticated(api).get(`business?${options}`);
    const getVendorCompanies = (options = '') => authenticated(api).get(`vr/companies?${options}`);
    const getAllVendorCompanies = (options = '') => authenticated(api).get(`vr/companies/all?${options}`);
    /**
     * @description get vr company stats
     * @param {number} companyId vr company id
     * @param {string} options 'date_start=2020-01-01&date_end=2020-12-3'
     */
    const getCompanyStats = (companyId, options) => authenticated(api).get(`vr/company/${companyId}/stats?${options}`);

    // add/remove company admin
    /**
     *
     * @param {*} type one of "add" and "remove"
     * @param {*} payload {"user_id":[42473],"company_id":195011}
     */
    const postVRCompanyAdmin = (type, payload) => authenticated(api).post(`vr/company/admin/${type}`, payload);

    /**
     *
     * @param {*} type one of "add" and "remove"
     * @param {*} payload {"blog_id": 1111, "user_id":9999}
     */
    const postBlogCompanyAdmin = (type, payload) => authenticated(api).post(`com/blog/admin/${type}`, payload);

    const getUserSubscriptions = (user_id) => authenticated(api).get(`subscriptions/${user_id}`);

    const getSubscriptionReports = () => authenticated(api).get('subscription/reports');

    const getSubscriptionFrequencies = () => authenticated(api).get('subscription/statuses');

    const postAddSubscription = (payload) => authenticated(api).post('subscription/add', payload);

    const postSaveSubscription = (payload) => authenticated(api).post('subscription/save', payload);

    /**
     * @description add a business into company
     * @param {string} type one of vr and hcm
     * @param {*} payload { "business_id": 1, "company_id": 5 }
     */
    const postAddBusiness = (type, payload) => authenticated(api).post(`business/${type}/add`, payload);

    // Save vendor rating
    const postSaveVendorRating = (payload) => authenticated(api).post('vr/rating/save', payload);

    // Save vendor avatar/cover photo
    const postSaveVRPhoto = (payload) => authenticated(api).post('cropper/com/crop', payload);

    // GET awards
    const getVendorAwards = () => authenticated(api).get('vr/awards');

    // Search
    const postGlobalSearch = (options) => authenticated(api).get(`search?${options}`);

    // Company profile blog
    const getComTopicCategories = () => authenticated(api).get('com/topic/categories');

    /**
     * @param payload {"blog": { "id": 1463, "entity_id": 37257, "meta": null, "data": { "categories": ["Fruits", "Colors", "Nukes"] }, "group_id": 2196 }}
     * @description endpoint to save blog and blog category
     */
    const postSaveBlog = (payload) => authenticated(api).post('com/blog/save', payload);

    /**
     *
     * @param {*} payload {"post": { "id": 234, "name": "post name", "entity_id": 36732, "status": 2, "data": { "body": "Post body message" } }}
     */
    const postSaveBlogPost = (payload) => authenticated(api).post('com/blog/post/save', payload);

    /**
     *
     * @param {*} payload { "post":{ "id":123, "blog_id": 4188, "user_id": 80821 } }
     * @description 3 fields post.id, post.user_id(community User), and either the post.blog_id or post.entity_id are required
     */
    const postDeleteBlogPost = (payload) => authenticated(api).post('com/blog/post/delete', payload);

    // Community
    const postForumTopic = (payload) => authenticated(api).post('com/topic/save', payload);
    const postSaveComment = (payload) => authenticated(api).post('com/comment/save', payload);
    const getUserPermissions = (userId) => authenticated(api).get(`users/${userId}/business/permissions`);

    /**
     * @description set flag such as like, dislike, and spam
     * @param {*} contentId
     * @param {*} flag one of like, dislike, and spam
     */
    const postFlagContent = (contentId, flag) => authenticated(api).get(`com/flag/${contentId}/${flag}`);

    /**
     * @description add content event such as click, impression, view
     * @param {*} payload { "events": [{ "content_id": 44, "source": "search", "type": "click" }] }
     */
    const postContentEvent = (payload) => authenticated(api).post('vr/events/content/add', payload);

    // GET Community user
    const getCommunityUser = (userId) => authenticated(api).get(`com/user?user_id=${userId}`);
    const getCommunityUserView = (userId) => authenticated(api).get(`com/user/view?user_id=${userId}`);
    const getCommunityBlog = (options) => authenticated(api).get(`com/blogs?${options}`);
    const getBlogSummary = (options) => authenticated(api).get(`com/blog/summary?${options}`);
    const getBlogDetail = (options) => authenticated(api).get(`com/blog?${options}`);
    const getBusinessBlog = (businessId, options = '') => authenticated(api).get(`com/blog?business_id=${businessId}&${options}`);

    // Save community user
    const postCommunityUser = (payload) => authenticated(api).post('com/user/save', payload);

    // Super Admin Search & Save user content
    /**
     * @description GET search user contents for super admin
     * @param {*} options Search options
     */
    const getSuperAdminUserContent = (options) => authenticated(api).get(`content/users?${options}`);
    /**
     * @description Post save user content for super admin
     * @param {*} payload user content to save
     */
    const postSuperAdminSaveUserContent = (payload) => authenticated(api).post('content/users/save', payload);
    /**
     * @description Post band user content for super admin
     * @param {*} payload { "user": { "id": 12 }, "reason" : "Spam articles & comments." }
     * @returns
     */
    const postSuperAdminBandUserContent = (payload) => authenticated(api).post('content/users/ban', payload);
    /**
     * @description Post restore user content for super admin
     * @param {*} payload { "user": { "id": 12 } }
     * @returns
     */
    const postSuperAdminRestoreUserContent = (payload) => authenticated(api).post('content/users/restore', payload);
    /**
     * @description GET business content for supper admin
     * @param {*} options
     */
    const getSuperAdminBusiness = (options) => authenticated(api).get(`content/businesses?${options}`);
    /**
     * @description GET categories content for supper admin
     * @param {*} options
     */
    const getSuperAdminCategories = (options) => authenticated(api).get(`content/community/categories?${options}`);
    /**
     * @description GET vendor ratings
     * @param {*} options
     */
    const getVendorRatings = (options) => authenticated(api).get(`content/ratings?${options}`);
    /**
     * @description GET content for super admin
     * @param {*} options search options
     */
    const getSuperAdminContent = (options) => authenticated(api).get(`content/search?${options}`);

    const postSaveUserContent = (payload) => authenticated(api).post('content/user/save', payload);

    /**
     * POST unfollow request
     * @param {*} payload {"company_id":44, "connect_company_id":195011}
     */
    const postUnfollowRequest = (payload) => authenticated(api).post('com/entity/unfollow', payload);
    /**
     * POST follow request
     * @param {*} payload {"company_id":446859, "connect_user_id":42701}
     */
    const postFollowRequest = (payload) => authenticated(api).post('com/entity/follow', payload);
    /**
     * POST connection update
     * @param {*} action one of active, rejected, delete
     * @param {*} payload {"connection_id":1200}
     */
    const postConnectionUpdate = ({ action, payload }) => authenticated(api).post(`com/entity/connection/${action}`, payload);

    // Upload file
    const getCommunityS3Info = () => authenticated(api).get('media/s3com');
    const postUploadfile = (url, payload) => s3API.post(url, payload);

    // Register community user
    const postRegisterUser = (payload) => authenticated(api).post('com/user/register', payload);

    /**
     * POST Delete Forums Topic
     * @param {*} payload
     * Example: {"topic": {"id":1234} }
     */
    const postDeleteForumTopic = (payload) => authenticated(api).post('com/topic/delete', payload);

    /**
     * @description Searches user employers for existing matches.
     * @param {string} options search options
     */
    const getEmployerSearch = (options) => authenticated(api).post(`com/employers?${options}`);

    return {
        postQuotaActualsSave,
        getCareerProgram,
        postCareerProgram,
        postCheckPassword,
        postTrainingReport,
        getGenerateHash,
        postPresentationSave,
        getActualsCount,
        getQuotasActuals,
        postQuotaActualComment,
        postQuotaActualComments,
        postSignOff,
        postTerminateEmployee,
        getVRCompany,
        getGetCompany,
        getGetCompanyWithDate,
        getRecentlyUpdatedProducts,
        getPopularProducts,
        postMultiCompanyEmployees,
        getGetBlog,
        postCreateBlog,
        postAddUser,
        postRemoveUser,
        postMultiCompanyData,
        postSaveProductMedia,
        deleteProductMedia,
        getAllAuthors,
        postSaveAuthor,
        postSaveBusinessAuthor,
        postDeleteAuthor,
        postCategorySave,
        postCategoryDelete,
        postCategoryRestore,
        postVendorRating,
        postAddEditDepartments,
        postDeleteDepartment,
        getSuperAdminBusiness,
        getSuperAdminContent,
        getGetBusiness,
        postAddEditTeams,
        postDeleteTeam,
        postSaveJobRoles,
        postSaveHCMCompany,
        getMinimalCompanyEmployees,
        getGetTopics,
        getAllDepartmentTopics,
        getEditFeaturedItems,
        getFeaturedItems,
        postSaveFeaturedItem,
        postArchiveFeaturedItem,
        getLearnFeeds,
        getCategorySponsoredProducts,
        getLibraryTemplatesSearch,
        getEmployeeSearch,
        getCompanyOrgChart,
        getSaveActualsReport,
        getCheckoutActuals,
        getCheckoutOrgChart,
        getUserJobDescription,
        getAnniversaryReports,
        getCompanySlug,
        getBlogSlug,

        // Deprecated API Definitions

        //////// App
        loginByEmail,
        getAuthoring,
        getCompanyVendor,
        getLibraryFilters,
        getCompanies,
        getCompany,
        getCompanyUsers,
        getNotifications,
        updateNotifications,
        postRecoveryPassword,

        //////// Develop

        // Learn
        getLearnFeed,
        getScormLink,
        updateCard,
        updateCardInstance,
        getCardTemplate,
        getAssignedCards,
        getQuizQuestion,
        submitAnswer,
        resetQuiz,
        updateModule,
        createCard,

        // Career
        getCareerMap,
        getCareerGoals,
        postCareerGoals,
        getCareerDevReports,
        getCareerReports,
        getCompanyCareers,
        requestPromote,

        // Reports
        getUserReports,
        getUsersCourseRequest,
        getTrainingReports,
        getFeedsReports,
        getCertificationReport,
        postHireOrientationReport,
        postQuotaReports,
        postTrainingCompetencyReports,
        postIndividualQuotaReport,
        postTrackReports,
        postTeamActivityReport,

        // Library
        postLibraryCardTemplates,
        getDevLibrarySearch,
        getDevLibraryCourses,
        getDevLibraryModules,
        getDevLibraryTracks,
        getDevLibraryCourseList,
        getDevLibraryModuleList,
        getDevLibraryTrackList,
        getDevLibrarySearchModules,
        postDevLibraryCardSave,
        postLibraryTrackSave,
        getDevReportTrainingList,
        getDevReportAuthors,
        postDevLibraryCreateQuota,
        postDevLibraryAddDocument,
        postDevLibraryDeleteDocument,
        getDevLibraryProgramsList,
        getLibrarySearch,
        getQuotaOptions,
        getLibraryList,
        getTemplateCheck,
        postDevLibraryEvent,
        postDeleteLibraryEvent,
        postScorecardSave,
        postDeleteScorecard,
        getScorecard,
        getTrackCheck,
        getQuotaCheck,
        getAssessments,
        getDailyFeed,
        getCheckout,
        postAddCheckoutComment,
        postCheckoutEdit,
        postCheckoutSignOff,
        postCheckoutSkipDay,
        postCheckoutReset,
        postCheckoutApprove,

        // Records
        postCompanyUsers,
        postDevRecordsCompany,
        getDevRecordsNotificationsSettings,
        postDevRecordsNotificationsSettings,
        postEditProfile,
        getUserCompanies,

        //////// Manage
        getTasksFeed,
        getUserHabits,
        getUserTasksCounts,
        getUsers,
        addComment,
        getProjectStats,
        getEmployees,
        getProjectTasks,
        getUserProjectTasks,
        getUserProjecthabits,
        getCompanyPerformanceReviews,
        getCompanyPerformanceReviewStats,
        getCompanyDailyCheckouts,
        getIndividualPerformanceReview,
        postPerformanceReview,
        postPerformanceReviewReset,
        getPerformanceReviews,
        getIndividualTasks,
        getManagerDashboard,
        getReportCheckouts,
        getIndividualCheckout,

        //Vendor
        getVendorCategories,
        getCategoryPopularProducts,
        getAllCategoryPopularProducts,
        getCategoryProducts,
        getVRProducts,
        getSuperAdminCategories,
        getVendorRatings,

        // Company
        postBusinessCompany,
        getCompanyBusiness,
        postSaveVendorRating,
        postSaveProduct,
        postSaveContent,
        postSaveVRCompany,
        postSaveVRPhoto,
        getVendorCompanies,
        getVendorCompaniesAlphabet,
        getAllVendorCompanies,
        getCompanyStats,
        getVendorAwards,
        postGlobalSearch,
        postVRCompanyAdmin,
        postBlogCompanyAdmin,
        getUserSubscriptions,
        getSubscriptionReports,
        getSubscriptionFrequencies,
        postAddSubscription,
        postSaveSubscription,

        // add business
        postAddBusiness,

        // add business
        postAddBusiness,

        //Community
        postForumTopic,
        postRegisterUser,
        postSaveComment,
        postFlagContent,
        postContentEvent,
        postDeleteForumTopic,

        // Get Community user
        getCommunityUser,
        getCommunityUserView,
        getCommunityBlog,
        getBlogSummary,
        getBlogDetail,
        getBusinessBlog,
        postCommunityUser,
        getCommunityS3Info,
        postUploadfile,
        getEntityConnections,
        postEntityConnection,
        postConnection,
        postFollowRequest,
        postUnfollowRequest,
        postConnectionUpdate,
        postSaveUserContent,
        getEmployerSearch,

        // Super admin
        getSuperAdminUserContent,
        postSuperAdminSaveUserContent,
        postSuperAdminBandUserContent,
        postSuperAdminRestoreUserContent,

        // Company profile blog
        getComBlogCategories,
        getComTopicCategories,
        getBlogPosts,
        postSaveBlog,
        postSaveBlogPost,
        postDeleteBlogPost,

        //Add Training
        getAuthors,
        getCourses,
        searchCourses,

        //Assign Training
        assignTraining,

        // assign scorecard
        assignScorecard,

        // unassign content
        postUnassignContent,

        // Add Project
        postMngProjectsAddNew,

        // Update Project
        postMngProjectsUpdate,

        // Delete Project
        postMngProjectsDelete,

        // File upload
        fileUpload,
        getS3Info,
        uploadAvatar,

        // Archived
        getArchivedFeeds,

        // Reports
        getUsertasks,
        getCompanytasks,
        postMngReportsEngagementReport,

        // Program
        postProgramEvent,
        getProgramRequestPromotion,
        getProgramStats,
        getUserPrograms,
        getUserScorecards,

        // Terminate, Unterminate and Delete user
        postTerminateUser,
        postDeleteUser,
        getUserPermissions,
    };
};

export default {
    create,
};
